import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClientApiError } from "../auth/api";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Select,
  Skeleton,
  Textarea,
  Toast,
} from "../components/ui";
import {
  getDogCompare,
  listDogModerationQueue,
  mergeDog,
  moderateDog,
  reviewDogEditRequest,
  type DogDetail,
  type DuplicateCandidate,
  type ModerationQueueItem,
} from "../dogs/api";
import { DogStatusBadge } from "../dogs/DogStatus";

interface CompareState {
  dog: DogDetail;
  latestVersion: {
    version: number;
    eventType: string;
    snapshot: Record<string, unknown>;
    patch: Record<string, unknown>;
    createdAt: string;
  } | null;
  pendingEditRequest: {
    id: string;
    baseVersion: number;
    patch: Record<string, unknown>;
    criticalFields: string[];
    requestedByUserId: string;
    createdAt: string;
  } | null;
  duplicates: DuplicateCandidate[];
}

const dateTime = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Ho_Chi_Minh",
});

function formatDate(value: string | null): string {
  if (!value) return "Chưa cập nhật";
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? value : dateTime.format(parsed);
}

function errorInfo(caught: unknown): { message: string; requestId?: string } {
  if (caught instanceof ClientApiError) {
    return { message: caught.problem.message, requestId: caught.problem.requestId };
  }
  return { message: "Không thể xử lý yêu cầu kiểm duyệt." };
}

function valueLabel(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Có" : "Không";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function ComparePatch({ compare }: { compare: CompareState }) {
  const patch = compare.pendingEditRequest?.patch ?? compare.latestVersion?.patch ?? {};
  const entries = Object.entries(patch);
  if (entries.length === 0) {
    return <p className="dog-moderation-empty-copy">Không có patch riêng; kiểm tra dữ liệu hồ sơ hiện tại.</p>;
  }
  return (
    <div className="dog-compare-table" role="table" aria-label="Dữ liệu thay đổi">
      <div className="dog-compare-row dog-compare-row--head" role="row">
        <span role="columnheader">Field</span>
        <span role="columnheader">Giá trị đề xuất</span>
      </div>
      {entries.map(([field, value]) => (
        <div className="dog-compare-row" role="row" key={field}>
          <strong role="cell">{field}</strong>
          <span role="cell">{valueLabel(value)}</span>
        </div>
      ))}
    </div>
  );
}

function DuplicateCompare({ candidates }: { candidates: DuplicateCandidate[] }) {
  if (candidates.length === 0) {
    return <p className="dog-moderation-empty-copy">Không có candidate trùng đáng kể.</p>;
  }
  return (
    <div className="dog-moderation-duplicates">
      {candidates.map((candidate) => (
        <article key={candidate.dog.id}>
          <div>
            <strong>{candidate.dog.registeredName}</strong>
            <small>
              {candidate.dog.breed?.nameVi ?? "Chưa rõ giống"} · {candidate.score} điểm
            </small>
          </div>
          <Badge tone={candidate.confidence === "near_certain" ? "danger" : "warning"}>
            {candidate.confidence === "near_certain" ? "Gần chắc chắn" : "Cần đối chiếu"}
          </Badge>
          <ul>
            {candidate.reasons.map((reason) => (
              <li key={reason.code}>{reason.labelVi} (+{reason.points})</li>
            ))}
          </ul>
          {candidate.dog.workflowStatus === "published" ? (
            <Link to={`/dogs/${candidate.dog.slug}`} target="_blank" rel="noreferrer">
              Mở hồ sơ candidate
            </Link>
          ) : null}
        </article>
      ))}
    </div>
  );
}

export function DogModerationPage() {
  const [items, setItems] = useState<ModerationQueueItem[]>([]);
  const [status, setStatus] = useState<"all" | "pending_review" | "disputed">("all");
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [compare, setCompare] = useState<CompareState | null>(null);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const page = await listDogModerationQueue({ status, cursor, limit: 25 });
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setError(null);
      if (selectedId && !page.items.some((item) => item.id === selectedId)) {
        setSelectedId(null);
        setCompare(null);
      }
    } catch (caught) {
      setError(errorInfo(caught));
    } finally {
      setLoadingQueue(false);
    }
  }, [cursor, selectedId, status]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const openCompare = useCallback(async (id: string) => {
    setSelectedId(id);
    setLoadingCompare(true);
    setError(null);
    try {
      const response = await getDogCompare(id);
      setCompare(response.compare);
      setMergeTargetId(response.compare.duplicates[0]?.dog.id ?? "");
      setNote("");
    } catch (caught) {
      setCompare(null);
      setError(errorInfo(caught));
    } finally {
      setLoadingCompare(false);
    }
  }, []);

  async function act(
    action: "approve" | "request_changes" | "reject" | "archive" | "dispute",
  ) {
    if (!compare) return;
    setBusy(true);
    setError(null);
    try {
      const response = await moderateDog(compare.dog.id, {
        action,
        note: note || null,
        expectedVersion: compare.dog.version,
      });
      setToast({
        title: action === "approve" ? "Đã publish hồ sơ" : "Đã xử lý hồ sơ",
        message: `Trạng thái mới: ${response.dog.workflowStatus}. Notification và audit đã được ghi.`,
      });
      setCompare(null);
      setSelectedId(null);
      await loadQueue();
    } catch (caught) {
      setError(errorInfo(caught));
    } finally {
      setBusy(false);
    }
  }

  async function reviewEdit(action: "approve" | "reject") {
    if (!compare?.pendingEditRequest) return;
    setBusy(true);
    setError(null);
    try {
      const response = await reviewDogEditRequest(compare.pendingEditRequest.id, {
        action,
        note: note || undefined,
        expectedDogVersion: compare.dog.version,
      });
      setToast({
        title: action === "approve" ? "Đã áp dụng edit request" : "Đã từ chối edit request",
        message: `Hồ sơ ${response.dog.registeredName} đã được cập nhật trong queue.`,
      });
      await openCompare(compare.dog.id);
      await loadQueue();
    } catch (caught) {
      setError(errorInfo(caught));
    } finally {
      setBusy(false);
    }
  }

  async function merge() {
    if (!compare || !mergeTargetId) return;
    setBusy(true);
    setError(null);
    try {
      const response = await mergeDog(compare.dog.id, {
        canonicalDogId: mergeTargetId,
        reason: note,
        expectedVersion: compare.dog.version,
      });
      setToast({
        title: "Đã gộp hồ sơ trùng",
        message: `Slug cũ chuyển về hồ sơ ${response.canonical.registeredName}.`,
      });
      setCompare(null);
      setSelectedId(null);
      await loadQueue();
    } catch (caught) {
      setError(errorInfo(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="dog-moderation-page">
      <header className="page-heading dog-page-heading">
        <div>
          <p className="eyebrow">Moderation · Dog Core</p>
          <h2>Hàng đợi hồ sơ chó</h2>
          <p>So sánh dữ liệu, duplicate evidence và edit request trước khi publish.</p>
        </div>
        <Badge tone="info">{items.length} hồ sơ trên trang</Badge>
      </header>

      {error ? (
        <ErrorState
          title="Không xử lý được yêu cầu"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Tải lại queue", onClick: () => void loadQueue() }}
        />
      ) : null}

      <div className="dog-moderation-layout">
        <aside className="dog-moderation-queue" aria-label="Danh sách chờ kiểm duyệt">
          <div className="dog-moderation-toolbar">
            <Select
              label="Loại queue"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as typeof status);
                setCursor(null);
              }}
            >
              <option value="all">Tất cả + edit request</option>
              <option value="pending_review">Chờ duyệt hồ sơ mới</option>
              <option value="disputed">Đang tranh chấp</option>
            </Select>
          </div>
          {loadingQueue ? <Skeleton lines={7} /> : null}
          {!loadingQueue && items.length === 0 ? (
            <EmptyState
              title="Queue đang trống"
              description="Không có hồ sơ hoặc edit request cần xử lý."
            />
          ) : null}
          {!loadingQueue ? items.map((item) => (
            <button
              key={item.id}
              type="button"
              className="dog-queue-item"
              aria-pressed={selectedId === item.id}
              onClick={() => void openCompare(item.id)}
            >
              <span className="dog-avatar-placeholder" aria-hidden="true">
                {item.registeredName.slice(0, 1).toUpperCase()}
              </span>
              <span className="dog-queue-item__body">
                <strong>{item.registeredName}</strong>
                <small>{item.breed?.nameVi ?? "Chưa rõ giống"} · {item.createdBy.displayName}</small>
                <span className="dog-queue-item__badges">
                  <DogStatusBadge status={item.workflowStatus} />
                  {item.pendingEditRequestId ? <Badge tone="info">Edit request</Badge> : null}
                  {item.duplicateDecision === "block" ? <Badge tone="danger">Duplicate block</Badge> : null}
                  {item.duplicateDecision === "review" ? <Badge tone="warning">Duplicate review</Badge> : null}
                </span>
              </span>
            </button>
          )) : null}
          <div className="dog-cursor-pagination">
            <Button variant="secondary" disabled={!cursor} onClick={() => setCursor(null)}>Trang đầu</Button>
            <Button variant="secondary" disabled={!nextCursor} onClick={() => setCursor(nextCursor)}>Trang sau</Button>
          </div>
        </aside>

        <main className="dog-moderation-detail">
          {loadingCompare ? <Skeleton lines={12} /> : null}
          {!loadingCompare && !compare ? (
            <EmptyState
              title="Chọn một hồ sơ"
              description="Panel so sánh sẽ hiển thị dữ liệu cốt lõi, patch và candidate trùng."
            />
          ) : null}
          {!loadingCompare && compare ? (
            <>
              <header className="dog-moderation-detail__header">
                <div>
                  <div className="dog-profile-badges">
                    <DogStatusBadge status={compare.dog.workflowStatus} />
                    {compare.dog.verified ? <Badge tone="success">Đã xác minh</Badge> : null}
                  </div>
                  <h3>{compare.dog.registeredName}</h3>
                  <p>{compare.dog.breed?.nameVi ?? "Chưa rõ giống"} · version {compare.dog.version}</p>
                </div>
                {compare.dog.workflowStatus === "published" ? (
                  <Link className="text-link" to={`/dogs/${compare.dog.slug}`} target="_blank" rel="noreferrer">
                    Mở profile
                  </Link>
                ) : null}
              </header>

              <section className="dog-moderation-card">
                <h4>Dữ liệu ưu tiên</h4>
                <dl className="dog-quick-grid dog-quick-grid--wide">
                  <div><dt>Tên</dt><dd>{compare.dog.registeredName}</dd></div>
                  <div><dt>Giống</dt><dd>{compare.dog.breed?.nameVi ?? "—"}</dd></div>
                  <div><dt>Ngày sinh</dt><dd>{formatDate(compare.dog.dateOfBirth)}</dd></div>
                  <div><dt>Giới tính</dt><dd>{compare.dog.sex}</dd></div>
                  <div><dt>Chó cha</dt><dd>{compare.dog.sire?.registeredName ?? "—"}</dd></div>
                  <div><dt>Chó mẹ</dt><dd>{compare.dog.dam?.registeredName ?? "—"}</dd></div>
                  <div><dt>Tổ chức</dt><dd>{compare.dog.registrationOrganization?.nameVi ?? "—"}</dd></div>
                  <div><dt>Số đăng ký</dt><dd>{compare.dog.registrationNumber ?? "—"}</dd></div>
                  <div><dt>Số phả hệ</dt><dd>{compare.dog.pedigreeNumber ?? "—"}</dd></div>
                  <div><dt>Chủ</dt><dd>{compare.dog.owner?.displayName ?? "—"}</dd></div>
                  <div><dt>Kennel</dt><dd>{compare.dog.kennel?.name ?? "—"}</dd></div>
                  <div><dt>Gửi lúc</dt><dd>{formatDate(compare.dog.submittedAt)}</dd></div>
                </dl>
              </section>

              <section className="dog-moderation-card">
                <h4>{compare.pendingEditRequest ? "Edit request đang chờ" : "Patch/version gần nhất"}</h4>
                {compare.pendingEditRequest ? (
                  <p>
                    Field trọng yếu: <strong>{compare.pendingEditRequest.criticalFields.join(", ")}</strong>
                  </p>
                ) : null}
                <ComparePatch compare={compare} />
                {compare.pendingEditRequest ? (
                  <div className="button-row">
                    <Button disabled={busy} onClick={() => void reviewEdit("approve")}>Duyệt edit request</Button>
                    <Button variant="danger" disabled={busy} onClick={() => void reviewEdit("reject")}>Từ chối edit request</Button>
                  </div>
                ) : null}
              </section>

              <section className="dog-moderation-card">
                <h4>Duplicate candidates</h4>
                <DuplicateCompare candidates={compare.duplicates} />
                {compare.duplicates.length > 0 ? (
                  <div className="dog-merge-controls">
                    <Select
                      label="Hồ sơ canonical"
                      value={mergeTargetId}
                      onChange={(event) => setMergeTargetId(event.target.value)}
                    >
                      <option value="">Chọn candidate</option>
                      {compare.duplicates
                        .filter((candidate) => candidate.dog.workflowStatus === "published")
                        .map((candidate) => (
                          <option key={candidate.dog.id} value={candidate.dog.id}>
                            {candidate.dog.registeredName} · {candidate.score} điểm
                          </option>
                        ))}
                    </Select>
                    <Button variant="danger" disabled={busy || !mergeTargetId || !note.trim()} onClick={() => void merge()}>
                      Gộp vào canonical
                    </Button>
                  </div>
                ) : null}
              </section>

              <section className="dog-moderation-card dog-moderation-actions">
                <Textarea
                  label="Ghi chú kiểm duyệt"
                  hint="Bắt buộc khi yêu cầu sửa, từ chối, archive, dispute hoặc merge."
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
                <div className="button-row">
                  {compare.dog.workflowStatus === "pending_review" || compare.dog.workflowStatus === "disputed" ? (
                    <Button disabled={busy} onClick={() => void act("approve")}>Approve & publish</Button>
                  ) : null}
                  {compare.dog.workflowStatus === "pending_review" || compare.dog.workflowStatus === "disputed" ? (
                    <Button variant="secondary" disabled={busy || !note.trim()} onClick={() => void act("request_changes")}>Yêu cầu chỉnh sửa</Button>
                  ) : null}
                  {compare.dog.workflowStatus === "pending_review" || compare.dog.workflowStatus === "disputed" || compare.dog.workflowStatus === "needs_changes" ? (
                    <Button variant="danger" disabled={busy || !note.trim()} onClick={() => void act("reject")}>Từ chối</Button>
                  ) : null}
                  {compare.dog.workflowStatus === "published" ? (
                    <Button variant="danger" disabled={busy || !note.trim()} onClick={() => void act("dispute")}>Đánh dấu tranh chấp</Button>
                  ) : null}
                  {compare.dog.workflowStatus !== "archived" ? (
                    <Button variant="ghost" disabled={busy || !note.trim()} onClick={() => void act("archive")}>Archive</Button>
                  ) : null}
                </div>
              </section>
            </>
          ) : null}
        </main>
      </div>
      {toast ? <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} /> : null}
    </section>
  );
}
